import type { Checker } from "./mapcheck";
import { surfaceAt, type Level } from "./level";

// Paintball targets (#137): things standing about the park to shoot with
// the paintball gun (#136). A map file's `targets` list places them (kind,
// spot, facing); each kind is a ball-shaped hit volume at some height over
// the ground there. They don't stop the skater (scenery, like the challenge
// markers). A hit bursts the target; it stays down until the map reloads
// (`PackState.burst`). Sim- and three-free; `render/targets.ts` draws them.

export type TargetKind = "can" | "sign" | "bullseye";

/** Each kind's hit radius and how high its middle is over the ground (m). */
export const TARGET_KINDS: Readonly<Record<TargetKind, { r: number; up: number }>> = {
  /** A paint can on the ground. */
  can: { r: 0.3, up: 0.25 },
  /** A round sign on a post, about head height. */
  sign: { r: 0.45, up: 1.5 },
  /** An archery-style bullseye on a stand. */
  bullseye: { r: 0.55, up: 1.1 },
};

/** At most this many targets on a map. */
export const MAX_TARGETS = 12;

/** A target in a map file: its kind, where it stands, and which way it faces (degrees, 0 faces +z). */
export interface MapTarget {
  kind: TargetKind;
  x: number;
  z: number;
  yaw: number;
}

/** A target as the sim has it: its middle (`y` over the ground it stands on) and hit radius. */
export interface Target {
  kind: TargetKind;
  x: number;
  y: number;
  z: number;
  /** Facing, radians. */
  yaw: number;
  r: number;
  /** The ground under it. */
  ground: number;
}

export function checkTargets(c: Checker, v: unknown, path: string, bounds: number): void {
  if (!Array.isArray(v) || v.length > MAX_TARGETS) {
    c.fail(path, `expected a list of at most ${MAX_TARGETS} targets`);
    return;
  }
  v.forEach((e, i) => {
    const at = `${path}[${i}]`;
    const o = c.obj(e, at);
    if (!o) return;
    c.only(o, ["kind", "x", "z", "yaw"], at);
    if (!Object.hasOwn(TARGET_KINDS, o.kind as string)) c.fail(`${at}.kind`, `expected one of ${Object.keys(TARGET_KINDS).join(", ")}`);
    const inside = { min: -bounds + 0.5, max: bounds - 0.5 };
    c.num(o, "x", at, inside);
    c.num(o, "z", at, inside);
    c.num(o, "yaw", at, { min: -360, max: 360 });
  });
}

/** Stands a map file's target on the level's ground. */
export function buildTarget(t: MapTarget, level: Level): Target {
  const k = TARGET_KINDS[t.kind];
  const ground = surfaceAt(level, t.x, t.z).h;
  return { kind: t.kind, x: t.x, y: ground + k.up, z: t.z, yaw: (t.yaw * Math.PI) / 180, r: k.r, ground };
}

/**
 * The first standing target (not in `burst`) the segment from (x0, y0, z0)
 * to (x1, y1, z1) passes through, and how far along it (0..1), or null.
 */
export function targetHit(
  targets: readonly Target[],
  burst: ReadonlySet<number>,
  x0: number,
  y0: number,
  z0: number,
  x1: number,
  y1: number,
  z1: number,
): { index: number; t: number } | null {
  const dx = x1 - x0;
  const dy = y1 - y0;
  const dz = z1 - z0;
  const a = dx * dx + dy * dy + dz * dz;
  if (a === 0) return null;
  let best: { index: number; t: number } | null = null;
  targets.forEach((tg, index) => {
    if (burst.has(index)) return;
    const fx = x0 - tg.x;
    const fy = y0 - tg.y;
    const fz = z0 - tg.z;
    const b = fx * dx + fy * dy + fz * dz;
    const c = fx * fx + fy * fy + fz * fz - tg.r * tg.r;
    // Already inside: hit at the start.
    if (c <= 0) {
      if (!best || best.t > 0) best = { index, t: 0 };
      return;
    }
    const disc = b * b - a * c;
    if (disc < 0) return;
    const t = (-b - Math.sqrt(disc)) / a;
    if (t < 0 || t > 1) return;
    if (!best || t < best.t) best = { index, t };
  });
  return best;
}

/** How far back from its row a map's targets range is (m), well inside `AIM_RANGE`. */
export const RANGE_BACK = 8;

/**
 * Where to stand to shoot a map's row (the targets challenge's start spot,
 * #137): `RANGE_BACK` out from the row's middle the way its targets face
 * on average, on the ground the row stands on (`floor`, the lowest if it's
 * on more than one), and that middle (`row`) to face. Null with no targets.
 */
export function targetRange(
  targets: readonly Target[],
): { x: number; z: number; floor: number; row: { x: number; z: number } } | null {
  if (!targets.length) return null;
  let x = 0;
  let z = 0;
  let fx = 0;
  let fz = 0;
  for (const t of targets) {
    x += t.x / targets.length;
    z += t.z / targets.length;
    fx += Math.sin(t.yaw);
    fz += Math.cos(t.yaw);
  }
  const f = Math.hypot(fx, fz) || 1;
  const floor = Math.min(...targets.map((t) => t.ground));
  return { x: x + (fx / f) * RANGE_BACK, z: z + (fz / f) * RANGE_BACK, floor, row: { x, z } };
}

/** Auto-aim reaches targets this far off the way you face (rad)... */
export const AIM_CONE = (10 * Math.PI) / 180;
/** ...and this far away (m). */
export const AIM_RANGE = 26;

/**
 * Auto-aim: the nearest standing target within `AIM_CONE` of heading `yaw`
 * from (x, z) and inside `AIM_RANGE`, or null.
 */
export function aimTarget(
  targets: readonly Target[],
  burst: ReadonlySet<number>,
  x: number,
  z: number,
  yaw: number,
): number | null {
  let best: number | null = null;
  let bestD = Infinity;
  targets.forEach((tg, i) => {
    if (burst.has(i)) return;
    const dx = tg.x - x;
    const dz = tg.z - z;
    const d = Math.hypot(dx, dz);
    if (d > AIM_RANGE || d < 0.5) return;
    let off = Math.atan2(dx, dz) - yaw;
    off = Math.atan2(Math.sin(off), Math.cos(off));
    if (Math.abs(off) > AIM_CONE || d >= bestD) return;
    best = i;
    bestD = d;
  });
  return best;
}
